import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usersApi, corporatesApi } from '../../services/api';
import {
    Users,
    Plus,
    Pencil,
    Trash2,
    X,
    Search,
    Mail,
    Phone,
    Shield,
    Building2,
    AlertCircle
} from 'lucide-react';

const UsersPage = () => {
    const { selectedCorporate } = useOutletContext();
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [corporates, setCorporates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        password: '',
        phoneNumber: '',
        roleId: 2,
        corporateIds: [],
        status: 1
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (selectedCorporate) {
            loadData();
        }
    }, [selectedCorporate]);

    const loadData = async () => {
        if (!selectedCorporate?.id) return;
        setLoading(true);
        setError(null);
        try {
            const [usersData, corporatesData] = await Promise.all([
                usersApi.getAll(selectedCorporate?.id),
                corporatesApi.getAll()
            ]);
            setUsers(usersData);
            setCorporates(corporatesData);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditingUser(null);
        setFormData({
            fullName: '',
            email: '',
            password: '',
            phoneNumber: '',
            roleId: 2,
            corporateIds: [],
            status: 1
        });
        setShowModal(true);
    };

    const handleEdit = (user) => {
        setEditingUser(user);
        setFormData({
            fullName: user.fullName || '',
            email: user.email || '',
            password: '',
            phoneNumber: user.phoneNumber || '',
            roleId: user.roleId,
            corporateIds: user.corporateIds || [],
            status: user.status !== undefined && user.status !== null ? user.status : 1
        });
        setShowModal(true);
    };

    const handleDelete = async (user) => {
        if (!window.confirm(`"${user.fullName}" kullanıcısını silmek istediğinizden emin misiniz?`)) {
            return;
        }

        try {
            await usersApi.delete(user.id);
            setUsers(users.filter(u => u.id !== user.id));
        } catch (err) {
            alert('Silme işlemi başarısız: ' + err.message);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            const submitData = { ...formData };
            if (editingUser && !submitData.password) {
                delete submitData.password;
            }

            if (editingUser) {
                const updated = await usersApi.update(editingUser.id, submitData);
                setUsers(users.map(u => u.id === editingUser.id ? updated : u));
            } else {
                const created = await usersApi.create(submitData);
                setUsers([...users, created]);
            }
            setShowModal(false);
        } catch (err) {
            alert('Kaydetme işlemi başarısız: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleCorporateToggle = (corporateId) => {
        const newIds = formData.corporateIds.includes(corporateId)
            ? formData.corporateIds.filter(id => id !== corporateId)
            : [...formData.corporateIds, corporateId];
        setFormData({ ...formData, corporateIds: newIds });
    };

    const getRoleName = (role) => {
        const roles = { 0: 'Süper Admin', 1: 'Yönetici', 2: 'Personel' };
        return roles[role] || 'Bilinmiyor';
    };

    const getRoleBadgeColor = (role) => {
        const colors = {
            0: 'bg-red-100 text-red-700',
            1: 'bg-blue-100 text-blue-700',
            2: 'bg-gray-100 text-gray-700'
        };
        return colors[role] || 'bg-gray-100 text-gray-700';
    };

    const getStatusBadge = (status) => {
        return status === 1 ? (
            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">
                Aktif
            </span>
        ) : (
            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                Pasif
            </span>
        );
    };

    const getCorporateNames = (corporateIds) => {
        if (!corporateIds || corporateIds.length === 0) return '-';
        return corporates
            .filter(c => corporateIds.includes(c.id))
            .map(c => c.name)
            .join(', ');
    };

    const filteredUsers = users.filter(user =>
        user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
            </div>
        );
    }

    return (
        <div>
            <div className="space-y-6">
                {/* Page Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Kullanıcılar</h1>
                        <p className="text-gray-500 mt-1">Tüm kullanıcıları yönetin</p>
                    </div>
                    <button
                        onClick={handleAdd}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Plus className="w-5 h-5" />
                        Yeni Kullanıcı
                    </button>
                </div>

                {/* Search */}
                <div className="bg-white rounded-xl shadow-sm border p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Ad veya e-posta ile ara..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                        <AlertCircle className="w-5 h-5 text-red-500" />
                        <p className="text-red-700">{error}</p>
                    </div>
                )}

                {/* Users Table */}
                <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                    {filteredUsers.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                            <Users className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                            <p>Kullanıcı bulunamadı.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            Kullanıcı
                                        </th>
                                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            E-POSTA
                                        </th>
                                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            Rol
                                        </th>
                                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            Kurumlar
                                        </th>
                                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            Durum
                                        </th>
                                        <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            İşlemler
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {filteredUsers.map((user) => (
                                        <tr key={user.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                                                        {user.fullName?.charAt(0).toUpperCase() || 'U'}
                                                    </div>
                                                    <span className="font-medium text-gray-900">{user.fullName}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                                        <Mail className="w-4 h-4 text-gray-400" />
                                                        {user.email}
                                                    </div>
                                                    {user.phoneNumber && (
                                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                                            <Phone className="w-4 h-4 text-gray-400" />
                                                            {user.phoneNumber}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(user.roleId)}`}>
                                                    <Shield className="w-3 h-3" />
                                                    {getRoleName(user.roleId)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2 text-sm text-gray-600 max-w-xs truncate">
                                                    <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                    <span className="truncate">{getCorporateNames(user.corporateIds)}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">{getStatusBadge(user.status)}</td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => handleEdit(user)}
                                                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                                        title="Düzenle"
                                                    >
                                                        <Pencil className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(user)}
                                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Sil"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white">
                            <h3 className="text-lg font-semibold text-gray-900">
                                {editingUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı'}
                            </h3>
                            <button
                                onClick={() => setShowModal(false)}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Ad Soyad *
                                </label>
                                <input
                                    type="text"
                                    value={formData.fullName}
                                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Örn: Ahmet Yılmaz"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    E-posta *
                                </label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Örn: ahmet@hastane.com"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Şifre {editingUser ? '(Değiştirmek için doldurun)' : '*'}
                                </label>
                                <input
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="••••••••"
                                    required={!editingUser}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Telefon
                                </label>
                                <input
                                    type="tel"
                                    value={formData.phoneNumber}
                                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Örn: 0532 123 45 67"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Rol *
                                </label>
                                <select
                                    value={formData.roleId}
                                    onChange={(e) => setFormData({ ...formData, roleId: parseInt(e.target.value) })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                >
                                    {currentUser?.roleId === 0 && <option value={0}>Süper Admin</option>}
                                    <option value={1}>Yönetici</option>
                                    <option value={2}>Personel</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    Durum
                                </label>
                                <div className="flex gap-6">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            checked={formData.status === 1}
                                            onChange={() => setFormData({ ...formData, status: 1 })}
                                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">Aktif</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            checked={formData.status === 0}
                                            onChange={() => setFormData({ ...formData, status: 0 })}
                                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">Pasif</span>
                                    </label>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Kurumlar
                                </label>
                                <div className="border border-gray-300 rounded-lg max-h-40 overflow-y-auto">
                                    {corporates.length === 0 ? (
                                        <p className="p-3 text-gray-500 text-sm">Kurum bulunamadı</p>
                                    ) : (
                                        corporates.map((corporate) => (
                                            <label
                                                key={corporate.id}
                                                className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={formData.corporateIds.includes(corporate.id)}
                                                    onChange={() => handleCorporateToggle(corporate.id)}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                />
                                                <span className="text-sm text-gray-700">{corporate.name}</span>
                                            </label>
                                        ))
                                    )}
                                </div>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    İptal
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                                >
                                    {saving ? 'Kaydediliyor...' : 'Kaydet'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsersPage;
